/*
 * ===============================================================================================================
 *                                Copyright 2024, Blue Yonder Group, Inc.
 *                                           All Rights Reserved
 *
 *                               THIS IS UNPUBLISHED PROPRIETARY SOURCE CODE OF
 *                                          BLUE YONDER GROUP, INC.
 *
 *
 *                         The copyright notice above does not evidence any actual
 *                                 or intended publication of such source code.
 *
 * ===============================================================================================================
 */
import { useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Typography } from '@mui/material';

import { AlertDialog } from '../../../../shared/Dialog/AlertDialog';
import { setDefaultPasswordPolicy } from '../../../../services/setting';
import { selectPasswordPolicies } from '../../../../store/passwordPolicies/passwordPoliciesSelector';
import { fetchPasswordPolicies } from '../../../../store/passwordPolicies/passwordPolicies';
import { displaySnackbar } from '../../../../store/snackBar/snackBarActionCreator';
import interpolateString from '../../../../utility/stringInterpolation';
import {
  DO_YOU_WANT_TO_SET,
  AS_THE_DEFAULT_POLICY,
  SET_AS_DEFAULT,
  CANCEL_BTN_TEXT,
  SET_AS_DEFAULT_SUCCESS,
  SET_AS_DEFAULT_ERROR
} from './constants';
import { PasswordPolicyModel } from '../../../../models/PasswordPolicy/PasswordPolicy';

interface SetAsDefaultDialogProps {
  passwordPolicyId: string;
  onClose: () => void;
}

const SetAsDefaultDialog = ({ passwordPolicyId, onClose }: SetAsDefaultDialogProps) => {
  const passwordPolicies = useSelector(selectPasswordPolicies);
  const dispatch = useDispatch();
  const [isRequesting, setIsRequesting] = useState(false);

  const passwordPolicy = passwordPolicies.find(
    (passwordPolicy: PasswordPolicyModel) => passwordPolicy.id === passwordPolicyId
  );

  const handleSubmit = async () => {
    setIsRequesting(true);

    try {
      const policy = await setDefaultPasswordPolicy(passwordPolicyId);
      fetchPasswordPolicies(dispatch);
      dispatch(displaySnackbar(interpolateString([policy.name], SET_AS_DEFAULT_SUCCESS), 'success'));
    } catch (error: any) {
      dispatch(displaySnackbar(`${SET_AS_DEFAULT_ERROR}: ${error}`, 'error'));
    }

    onClose();
  };

  return (
    <AlertDialog
      dialogContentText={SET_AS_DEFAULT}
      dialogTitle={`${DO_YOU_WANT_TO_SET} `}
      recordName={passwordPolicy?.name}
      additionalContent={<Typography component="span"> {AS_THE_DEFAULT_POLICY}</Typography>}
      primaryButtonText={SET_AS_DEFAULT}
      primaryButtonColor="primary"
      secondButtonText={CANCEL_BTN_TEXT}
      handleOnCancel={onClose}
      handleOnOk={handleSubmit}
      showDialogLoader={isRequesting}
      isStatement={true}
      hideCanNotBeUndoneMessage={true}
    />
  );
};

export default SetAsDefaultDialog;
